import { useEffect, useMemo, useState } from "react";
import { Undo2, Loader2, Info, Lock, Search, ListChecks, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TRY, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ReturnSearch } from "@/components/returns/ReturnSearch";
import { ReturnLinesTable } from "@/components/returns/ReturnLinesTable";
import { SupplierCatalog } from "@/components/returns/SupplierCatalog";
import { ScanBanner } from "@/components/returns/ScanBanner";
import { pickOcc, occKey, r2, defaultQty } from "@/components/returns/util";

let UID = 0;
const ALL = "__all__";
const newLine = (g, occ) => ({ uid: ++UID, vkn: g.vkn, supplier: g.supplier, name: g.name, code: g.code, unit: g.unit, occs: g.occurrences, occ, qty: defaultQty(occ) });

function Totals({ lines }) {
  const t = lines.reduce((a, l) => { const net = r2((Number(l.qty) || 0) * l.occ.net_unit); const tax = r2(net * l.occ.tax_rate / 100); return { net: a.net + net, tax: a.tax + tax }; }, { net: 0, tax: 0 });
  return (
    <div className="rounded-xl bg-[#0B192C] text-white px-4 py-3 min-w-[240px]" data-testid="ret-totals">
      <div className="flex justify-between text-xs text-slate-300"><span>{lines.length} kalem · Net</span><span className="tabular-nums">{TRY(t.net)}</span></div>
      <div className="flex justify-between text-xs text-slate-300 mt-0.5"><span>KDV</span><span className="tabular-nums">{TRY(t.tax)}</span></div>
      <div className="flex justify-between items-center mt-1.5 border-t border-white/10 pt-1.5"><span className="text-[11px] font-bold uppercase tracking-wider text-orange-300">İade Toplamı</span><span className="font-head font-black text-xl tabular-nums" data-testid="ret-total">{TRY(t.net + t.tax)}</span></div>
    </div>
  );
}

function TabBtn({ on, onClick, icon: Icon, children, testid }) {
  return <button type="button" onClick={onClick} className={cn("inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-semibold transition-colors", on ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")} data-testid={testid}><Icon className="h-3.5 w-3.5" />{children}</button>;
}

/** TOPLU İADE FATURASI — ürün kodundan en son alış faturası bulunur; farklı faturalar tek iade faturasında referanslanır. Stok/cari değişmez. */
export default function BulkReturnDialog({ open, onClose, onCreated, editInv = null }) {
  const [suppliers, setSuppliers] = useState([]);
  const [vkn, setVkn] = useState("");
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("search");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ver, setVer] = useState(0);
  const editId = editInv?.id || "";

  const loadSuppliers = () => api.get("/qnb/returns/suppliers").then((r) => setSuppliers(r.data)).catch((e) => toast.error(apiError(e)));
  // Düzenleme: kayıtlı taslaktaki kalemler (fiyat geçmişi + seçili fatura + miktar) yüklenir
  const loadDraft = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/qnb/returns/draft/${editId}`);
      setLines(data.lines.map((x) => {
        const occ = x.group.occurrences.find((o) => o.incoming_id === x.incoming_id && o.index === x.index);
        return { ...newLine(x.group, occ), qty: x.quantity };
      }));
      setNote(data.note || "");
      if (data.missing) toast.error(`${data.missing} kalemin alış faturası bulunamadı — listeye eklenmedi`);
    } catch (e) { toast.error(apiError(e)); onClose && onClose(); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!open) return;
    setLines([]); setNote(""); setVkn(""); setTab("search"); loadSuppliers();
    if (editId) loadDraft();
  }, [open, editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const lockedVkn = lines[0]?.vkn || "";
  const activeVkn = lockedVkn || vkn;

  const addMany = (groups) => {
    let next = [...lines];
    let added = 0;
    for (const g of groups) {
      const occ = pickOcc(g);
      const lock = next[0]?.vkn;
      if (!occ) continue;
      if (lock && g.vkn !== lock) { toast.error(`${g.name} başka tedarikçiden (${g.supplier}) — tek iade faturasında tek tedarikçi olur`); continue; }
      const ex = next.find((l) => occKey(l.occ) === occKey(occ));
      if (ex) next = next.map((l) => (l === ex ? { ...l, qty: r2(Number(l.qty || 0) + defaultQty(occ)) } : l));
      else next = [...next, newLine(g, occ)];
      added += 1;
    }
    setLines(next);
    return added > 0;
  };
  const addOne = (g) => addMany([g]);
  const update = (uid, patch) => setLines((ls) => ls.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  const remove = (uid) => setLines((ls) => ls.filter((l) => l.uid !== uid));
  const switchOcc = (line, occ) => {
    if (!occ) return;
    if (lines.some((l) => l.uid !== line.uid && occKey(l.occ) === occKey(occ))) return toast.error("Bu fatura kalemi listede zaten var");
    update(line.uid, { occ });
  };

  const rates = useMemo(() => [...new Set(lines.map((l) => l.occ.tax_rate))], [lines]);
  const refs = useMemo(() => { const m = new Map(); lines.forEach((l) => m.set(l.occ.incoming_id, l.occ)); return [...m.values()].sort((a, b) => (a.date > b.date ? 1 : -1)); }, [lines]);
  const invalid = lines.some((l) => !(Number(l.qty) > 0));

  const create = async () => {
    setBusy(true);
    try {
      const body = { lines: lines.map((l) => ({ incoming_id: l.occ.incoming_id, index: l.occ.index, quantity: Number(l.qty) })), note };
      const { data } = editId ? await api.put(`/qnb/returns/bulk-draft/${editId}`, body) : await api.post("/qnb/returns/bulk-draft", body);
      toast.success(`${editId ? "Toplu iade taslağı güncellendi" : "Toplu iade faturası taslağı oluşturuldu"} · ${TRY(data.invoice.total)} — önizleyip kesebilirsiniz`);
      onCreated && onCreated(data.invoice);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose && onClose(); }}>
      <DialogContent className="max-w-[98vw] sm:max-w-6xl max-h-[95vh] overflow-y-auto p-0 gap-0" data-testid="bulk-return-dialog">
        <DialogHeader className="px-5 py-3 bg-gradient-to-br from-orange-600 to-orange-500 text-white rounded-t-lg">
          <DialogTitle className="font-head text-xl flex items-center gap-2 text-white">
            <span className="h-8 w-8 rounded-xl bg-white/15 flex items-center justify-center"><Undo2 className="h-4 w-4" /></span>
            Toplu İade Faturası{editId && <span className="ml-1 rounded-md bg-white/20 px-2 py-0.5 text-xs font-bold" data-testid="ret-edit-badge">TASLAK DÜZENLE · ürün ekle / çıkar</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">Tedarikçi</span>
            {lockedVkn ? (
              <div className="flex-1 min-w-0 h-9 rounded-lg border bg-muted/40 px-3 flex items-center gap-2 text-sm" data-testid="ret-supplier-locked">
                <Lock className="h-3.5 w-3.5 text-orange-600 shrink-0" /><b className="truncate">{lines[0].supplier}</b><span className="text-xs text-muted-foreground shrink-0">VKN {lockedVkn}</span>
              </div>
            ) : (
              <Select value={vkn || ALL} onValueChange={(v) => setVkn(v === ALL ? "" : v)}>
                <SelectTrigger className="flex-1 h-9" data-testid="ret-supplier-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  <SelectItem value={ALL}>Tüm tedarikçiler — ürün kodundan otomatik bulunur</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.vkn} value={s.vkn}>{s.name} · {s.total} fatura{s.last ? ` · son ${fmtDate(s.last)}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <ScanBanner open={open} vkn={activeVkn} onDone={() => { loadSuppliers(); setVer((v) => v + 1); }} />
          <div className="inline-flex rounded-xl bg-muted p-1 gap-1" data-testid="ret-tabs">
            <TabBtn on={tab === "search"} onClick={() => setTab("search")} icon={Search} testid="ret-tab-search">Kod / barkod ile ekle</TabBtn>
            <TabBtn on={tab === "catalog"} onClick={() => setTab("catalog")} icon={ListChecks} testid="ret-tab-catalog">Tedarikçiden aldıklarım</TabBtn>
          </div>
          {tab === "search" ? <ReturnSearch vkn={activeVkn} ver={ver} exclude={editId} onAdd={addOne} /> : <SupplierCatalog vkn={activeVkn} ver={ver} exclude={editId} onAddMany={addMany} />}
          {loading ? <div className="rounded-xl border py-10 grid place-items-center text-sm text-muted-foreground" data-testid="ret-draft-loading"><Loader2 className="h-5 w-5 animate-spin mb-1" />Taslak yükleniyor…</div>
            : <ReturnLinesTable lines={lines} onUpdate={update} onRemove={remove} onSwitch={switchOcc} />}
          {rates.length > 1 && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex gap-2" data-testid="ret-mixed-kdv">
              <AlertTriangle className="h-4 w-4 shrink-0" />Farklı KDV oranlı kalemler (%{rates.join(", %")}) aynı iade faturasına konamaz — her oran için ayrı iade faturası kesin.
            </div>
          )}
          <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
            <div className="flex-1 space-y-2">
              {refs.length > 0 && (
                <div className="rounded-xl border bg-orange-50/60 border-orange-200 px-3 py-2 text-xs" data-testid="ret-refs">
                  <b className="text-orange-800">{refs.length} alış faturasına iade:</b>{" "}
                  {refs.map((o) => `${o.belge_no} (${fmtDate(o.date)})`).join(", ")}
                </div>
              )}
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="İade nedeni / not (faturaya yazılır, opsiyonel) — ör. arızalı ürün iadesi" className="text-sm" data-testid="ret-note" />
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="ret-ledger-note"><Info className="h-3.5 w-3.5 shrink-0" />Yalnızca iade faturası kesilir — stok ve cari bakiyesi değişmez.</div>
            </div>
            <Totals lines={lines} />
          </div>
        </div>
        <DialogFooter className="px-4 pb-4 gap-2 flex-row flex-wrap items-center">
          {lines.length > 0 && <Button variant="ghost" size="sm" onClick={() => setLines([])} disabled={busy} data-testid="ret-clear">Listeyi temizle</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onClose && onClose()} disabled={busy} data-testid="ret-cancel">Vazgeç</Button>
          <Button className="bg-orange-600 hover:bg-orange-700 max-sm:w-full" onClick={create} disabled={busy || loading || !lines.length || invalid || rates.length > 1} data-testid="ret-create">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1.5" />}<span className="sm:hidden">{editId ? "Taslağı Güncelle" : "Taslağı Oluştur"}</span><span className="hidden sm:inline">{editId ? "Taslağı Güncelle → Önizle & Kes" : "Taslağı Oluştur → Önizle & Kes"}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
